/**
 * Operações Web de caixas de picking — módulo compartilhado.
 *
 * Usado pela Gestão de Caixas, tela de Saídas e Conferência de Saída.
 * Controle de caixa picking (out_enable_box_control) é independente do
 * picking otimizado / hierarquia de embalagens (pkg_*).
 */

import api from '../api.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';

export const STATUS_BOX_LABEL = { 10: 'Aberta', 20: 'Lacrada', 30: 'Cancelada' };
export const STATUS_BOX_STYLE = {
	10: 'background:#d4edda;color:#155724',
	20: 'background:#cce5ff;color:#004085',
	30: 'background:#f8d7da;color:#721c24',
};

/** Texto operacional sobre separação entre controles. */
export const BOX_CTRL_HINT =
	'O controle de caixas (#MT-9448740) é usado na Conferência. É independente do picking otimizado e das embalagens secundárias/terciárias.';

/**
 * @param {*} v
 * @returns {string}
 */
export function escHtml(v) {
	if (v == null) return '';
	return String(v)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * @param {*} v
 * @returns {string}
 */
export function fmtDt(v) {
	if (!v) return '—';
	try {
		const d = new Date(v);
		return d.toLocaleString('pt-BR', {
			day: '2-digit', month: '2-digit', year: '2-digit',
			hour: '2-digit', minute: '2-digit',
		});
	} catch (_) { return String(v); }
}

/**
 * Soma quantidades por linha em caixas não canceladas, opcionalmente excluindo uma caixa.
 * @param {Array} boxes
 * @param {string|null} excludeBoxId
 * @returns {Record<number, number>}
 */
export function sumQtyByLine(boxes, excludeBoxId = null) {
	const sums = {};
	for (const box of boxes || []) {
		if (Number(box.status) === 30) continue;
		if (excludeBoxId && String(box.id) === String(excludeBoxId)) continue;
		for (const item of (box.items || [])) {
			const num = Number(item.output_item_number);
			sums[num] = (sums[num] || 0) + (Number(item.quantity) || 0);
		}
	}
	return sums;
}

/**
 * Modal de confirmação para cancelar caixa.
 * @param {string} companyId
 * @param {number|string} outputId
 * @param {string} boxId
 * @param {string} boxCode
 * @param {function} [onSuccess]
 */
export function openCancelBoxModal(companyId, outputId, boxId, boxCode, onSuccess) {
	const html = `
		<div class="msgbox" style="min-width:360px;max-width:520px">
			<div class="mb-title">Cancelar Caixa</div>
			<div class="mb-text">
				<p style="margin:0 0 0.75rem">
					Confirma o cancelamento da caixa <strong>${escHtml(boxCode)}</strong>?
				</p>
				<p style="margin:0 0 0.5rem;color:#888;font-size:0.92em">
					Os itens serão mantidos para auditoria. Esta ação não pode ser desfeita.
				</p>
				<label style="display:block;font-size:0.9em;margin-top:0.75rem">
					Observação (opcional):
					<textarea id="cancel-box-notes" rows="2"
						style="display:block;width:100%;margin-top:0.25rem;box-sizing:border-box;padding:0.3rem;font-size:0.9em;resize:vertical"
						placeholder="Motivo do cancelamento..."></textarea>
				</label>
			</div>
			<button type="button" class="button mtl bn_N">Voltar</button>
			<button type="button" class="button mtl bn_Y">Confirmar</button>
		</div>
	`;

	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key === 'Y') {
			const notes = dlg.getNode().querySelector('#cancel-box-notes')?.value?.trim() || null;
			dlg.destroy();
			try {
				const result = await api.call(api.cancel_output_picking_box, companyId, outputId, boxId, { notes });
				if (result?.error) {
					notify(result.error, 'Erro ao Cancelar');
					return;
				}
				notify(`Caixa "${escHtml(boxCode)}" cancelada com sucesso.`, 'Caixa Cancelada');
				if (typeof onSuccess === 'function') onSuccess();
			} catch (e) {
				notify(e?.message || String(e), 'Erro');
			}
		}
	}, /Enter|Escape|N|Y/);
}

/**
 * Abre modal para criar caixa de picking vinculada a uma saída.
 * @param {string} companyId
 * @param {number|string|null} outputId - se null, exibe campo para informar saída
 * @param {function} [onSuccess]
 * @param {{ initialOutputId?: string|number }} [opts]
 */
export function openCreateBoxModal(companyId, outputId, onSuccess, opts = {}) {
	const presetOutput = outputId || opts.initialOutputId || '';
	const showOutputField = !presetOutput;

	const html = `
		<div class="msgbox" style="min-width:400px;max-width:560px">
			<div class="mb-title">Abrir Caixa</div>
			<div class="mb-text">
				<p style="margin:0 0 0.75rem;color:#666;font-size:0.88em">${escHtml(BOX_CTRL_HINT)}</p>
				${showOutputField ? `
				<label style="display:block;font-size:0.9em;margin-bottom:0.6rem">
					Saída<sup>*</sup>
					<input id="pkbox-create-output" type="number" min="1" step="1"
						style="display:block;width:100%;margin-top:0.25rem;box-sizing:border-box;padding:0.35rem"
						placeholder="Número da saída" autocomplete="off">
				</label>` : `<input type="hidden" id="pkbox-create-output" value="${escHtml(presetOutput)}">`}
				<label style="display:block;font-size:0.9em;margin-bottom:0.6rem">
					Código da caixa<sup>*</sup>
					<input id="pkbox-create-code" type="text" maxlength="40"
						style="display:block;width:100%;margin-top:0.25rem;box-sizing:border-box;padding:0.35rem"
						placeholder="Ex.: 00001" autocomplete="off">
				</label>
				<label style="display:block;font-size:0.9em">
					Observação (opcional)
					<textarea id="pkbox-create-notes" rows="2"
						style="display:block;width:100%;margin-top:0.25rem;box-sizing:border-box;padding:0.35rem;font-size:0.9em;resize:vertical"
						placeholder="Observações sobre a caixa..."></textarea>
				</label>
				<p style="margin:0.75rem 0 0;color:#888;font-size:0.82em">
					O número do lacre é opcional e pode ser informado ao lacrar a caixa.
				</p>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Abrir Caixa</button>
		</div>
	`;

	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key !== 'Y') return;

		const node = dlg.getNode();
		const outRaw = node.querySelector('#pkbox-create-output')?.value?.trim();
		const code   = node.querySelector('#pkbox-create-code')?.value?.trim();
		const notes  = node.querySelector('#pkbox-create-notes')?.value?.trim() || null;

		if (!outRaw) {
			notify('Informe o número da saída.', 'Abrir Caixa');
			node.querySelector('#pkbox-create-output')?.focus();
			return;
		}
		if (!code) {
			notify('Informe o código da caixa.', 'Abrir Caixa');
			node.querySelector('#pkbox-create-code')?.focus();
			return;
		}

		const outId = parseInt(outRaw, 10);
		if (!Number.isFinite(outId) || outId < 1) {
			notify('Número da saída inválido.', 'Abrir Caixa');
			return;
		}

		try {
			const result = await api.call(api.create_output_picking_box, companyId, outId, { code, notes });
			if (result?.error) {
				notify(result.error, 'Erro ao Abrir Caixa');
				return;
			}
			dlg.destroy();
			notify(`Caixa "${escHtml(code)}" aberta com sucesso.`, 'Caixa Aberta');
			if (typeof onSuccess === 'function') onSuccess(result, outId);
		} catch (e) {
			notify(e?.message || String(e), 'Erro');
		}
	}, /Enter|Escape|N|Y/);

	const codeInput = dlg.getNode().querySelector('#pkbox-create-code');
	if (codeInput) codeInput.focus();
}

/**
 * Modal para adicionar/substituir itens em caixa aberta (quantidade consolidada por linha).
 * @param {string} companyId
 * @param {number|string} outputId
 * @param {string} boxId
 * @param {string} boxCode
 * @param {function} [onSuccess]
 */
export async function openAddItemsModal(companyId, outputId, boxId, boxCode, onSuccess) {
	let output;
	let boxes;
	try {
		[output, boxes] = await Promise.all([
			api.call(api.select_output, companyId, outputId),
			api.call(api.list_output_picking_boxes, companyId, outputId).then(r => r?.boxes || []),
		]);
	} catch (e) {
		notify(e?.message || 'Erro ao carregar dados da saída.', 'Adicionar Itens');
		return;
	}

	if (!output?.items?.length) {
		notify('A saída não possui itens para alocar na caixa.', 'Adicionar Itens');
		return;
	}

	const box = (boxes || []).find(b => String(b.id) === String(boxId));
	if (!box || Number(box.status) !== 10) {
		notify('Somente caixas abertas permitem adicionar itens.', 'Adicionar Itens');
		return;
	}

	const qtyOthers = sumQtyByLine(boxes, boxId);
	const qtyInBox = {};
	for (const item of (box.items || [])) {
		qtyInBox[Number(item.output_item_number)] = Number(item.quantity) || 0;
	}

	const rows = output.items.map(item => {
		const num = Number(item.number);
		const required = Number(item.quantity_required) || 0;
		const inOthers = qtyOthers[num] || 0;
		const inBox = qtyInBox[num] || 0;
		const available = Math.max(0, required - inOthers);
		const sku = item.product_sku ? String(item.product_sku).trim() : '';
		const name = item.product_name ? String(item.product_name).trim() : `Produto #${item.product_id}`;
		const lot = item.lot_number ? String(item.lot_number).trim() : '—';
		const disabled = available <= 0 && inBox <= 0;

		return `<tr data-line="${num}"${disabled ? ' style="opacity:0.55"' : ''}>
			<td style="padding:4px 6px">${num}</td>
			<td style="padding:4px 6px">
				<div style="font-size:0.72rem;color:#666">${escHtml(sku)}</div>
				<div>${escHtml(name)}</div>
			</td>
			<td style="padding:4px 6px">${escHtml(lot)}</td>
			<td style="padding:4px 6px;text-align:right">${required}</td>
			<td style="padding:4px 6px;text-align:right">${inOthers}</td>
			<td style="padding:4px 6px;text-align:right;font-weight:600">${available}</td>
			<td style="padding:4px 6px;text-align:right">
				<input type="number" class="pkbox-item-qty" data-line="${num}" data-max="${available + inBox}"
					min="0" max="${available + inBox}" step="1" value="${inBox}"
					${disabled ? 'disabled' : ''}
					style="width:5rem;text-align:right;padding:2px 4px">
			</td>
		</tr>`;
	}).join('');

	const html = `
		<div class="msgbox" style="min-width:640px;max-width:920px">
			<div class="mb-title">Adicionar Itens — Caixa ${escHtml(boxCode)}</div>
			<div class="mb-text" style="max-height:70vh;overflow-y:auto">
				<p style="margin:0 0 0.5rem;color:#666;font-size:0.85em">
					Informe a quantidade consolidada por linha nesta caixa. O saldo disponível considera as demais caixas não canceladas.
				</p>
				<div style="overflow-x:auto">
					<table style="width:100%;border-collapse:collapse;font-size:0.86em">
						<thead>
							<tr style="background:#f0f0f0">
								<th style="padding:4px 6px;text-align:left">Linha</th>
								<th style="padding:4px 6px;text-align:left">Produto</th>
								<th style="padding:4px 6px;text-align:left">Lote sep.</th>
								<th style="padding:4px 6px;text-align:right">Req.</th>
								<th style="padding:4px 6px;text-align:right">Outras cx.</th>
								<th style="padding:4px 6px;text-align:right">Disponível</th>
								<th style="padding:4px 6px;text-align:right">Nesta caixa</th>
							</tr>
						</thead>
						<tbody>${rows}</tbody>
					</table>
				</div>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Salvar Itens</button>
		</div>
	`;

	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key !== 'Y') return;

		const node = dlg.getNode();
		const itemsPayload = [];
		for (const input of node.querySelectorAll('.pkbox-item-qty:not([disabled])')) {
			const line = parseInt(input.dataset.line, 10);
			const max  = parseInt(input.dataset.max, 10);
			const raw  = input.value.trim();
			if (raw === '' || isNaN(Number(raw))) continue;
			const qty = parseInt(raw, 10);
			if (qty <= 0) continue;
			if (qty > max) {
				notify(`Linha ${line}: quantidade (${qty}) ultrapassa o saldo disponível (${max}).`, 'Adicionar Itens');
				input.focus();
				return;
			}
			const orig = output.items.find(i => Number(i.number) === line);
			itemsPayload.push({
				output_item_number: line,
				quantity: qty,
				lot_id: orig?.lot_id ?? null,
				sku: orig?.product_sku ?? null,
			});
		}

		if (!itemsPayload.length) {
			notify('Informe ao menos uma linha com quantidade maior que zero.', 'Adicionar Itens');
			return;
		}

		try {
			const result = await api.call(
				api.set_output_picking_box_items,
				companyId, outputId, boxId,
				{ items: itemsPayload }
			);
			if (result?.error) {
				notify(result.error, 'Erro ao Salvar Itens');
				return;
			}
			dlg.destroy();
			notify('Itens salvos na caixa com sucesso.', 'Adicionar Itens');
			if (typeof onSuccess === 'function') onSuccess(result);
		} catch (e) {
			notify(e?.message || String(e), 'Erro');
		}
	}, /Enter|Escape|N|Y/);
}

/**
 * Modal para lacrar caixa aberta.
 * @param {string} companyId
 * @param {number|string} outputId
 * @param {string} boxId
 * @param {string} boxCode
 * @param {number} [itemCount]
 * @param {function} [onSuccess]
 * @param {{ initialSeal?: string }} [opts]
 */
export function openCloseBoxModal(companyId, outputId, boxId, boxCode, itemCount, onSuccess, opts = {}) {
	const initialSeal = (opts.initialSeal || '').trim();
	const initialSeal2 = (opts.initialSeal2 || '').trim();
	const html = `
		<div class="msgbox" style="min-width:380px;max-width:520px">
			<div class="mb-title">Lacrar Caixa</div>
			<div class="mb-text">
				<p style="margin:0 0 0.75rem">
					Lacrar a caixa <strong>${escHtml(boxCode)}</strong>?
					Após lacrar, não será possível adicionar ou alterar itens.
				</p>
				<p style="margin:0 0 0.75rem;color:#555;font-size:0.88em">
					Informe os <strong>2 lacres</strong> usados para fechar a caixa.
				</p>
				${itemCount === 0 ? `<p style="margin:0 0 0.5rem;color:#c00;font-size:0.9em">
					⚠ Esta caixa não possui itens — o lacre será bloqueado.
				</p>` : ''}
				<label style="display:block;font-size:0.9em;margin-bottom:0.6rem">
					Lacre 1<sup>*</sup>
					<input id="pkbox-close-seal" type="text" maxlength="40"
						style="display:block;width:100%;margin-top:0.25rem;box-sizing:border-box;padding:0.35rem"
						placeholder="1º lacre" autocomplete="off"
						value="${escHtml(initialSeal)}">
				</label>
				<label style="display:block;font-size:0.9em;margin-bottom:0.6rem">
					Lacre 2<sup>*</sup>
					<input id="pkbox-close-seal-2" type="text" maxlength="40"
						style="display:block;width:100%;margin-top:0.25rem;box-sizing:border-box;padding:0.35rem"
						placeholder="2º lacre" autocomplete="off"
						value="${escHtml(initialSeal2)}">
				</label>
				<label style="display:block;font-size:0.9em">
					Observação (opcional)
					<textarea id="pkbox-close-notes" rows="2"
						style="display:block;width:100%;margin-top:0.25rem;box-sizing:border-box;padding:0.35rem;resize:vertical"
						placeholder="Observações..."></textarea>
				</label>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Lacrar Caixa</button>
		</div>
	`;

	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key !== 'Y') return;

		const node = dlg.getNode();
		const seal_code = node.querySelector('#pkbox-close-seal')?.value?.trim() || null;
		const seal_code_2 = node.querySelector('#pkbox-close-seal-2')?.value?.trim() || null;
		const notes     = node.querySelector('#pkbox-close-notes')?.value?.trim() || null;

		if (!seal_code || !seal_code_2) {
			notify('Informe os dois lacres da caixa.', 'Lacrar Caixa');
			node.querySelector(!seal_code ? '#pkbox-close-seal' : '#pkbox-close-seal-2')?.focus();
			return;
		}
		if (seal_code.toUpperCase() === seal_code_2.toUpperCase()) {
			notify('Os dois lacres devem ser diferentes.', 'Lacrar Caixa');
			node.querySelector('#pkbox-close-seal-2')?.focus();
			return;
		}

		try {
			const result = await api.call(
				api.close_output_picking_box,
				companyId, outputId, boxId,
				{ seal_code, seal_code_2, notes }
			);
			if (result?.error) {
				notify(result.error, 'Erro ao Lacrar Caixa');
				return;
			}
			dlg.destroy();
			notify(`Caixa "${escHtml(boxCode)}" lacrada com sucesso.`, 'Caixa Lacrada');
			if (typeof onSuccess === 'function') onSuccess(result);
		} catch (e) {
			notify(e?.message || String(e), 'Erro');
		}
	}, /Enter|Escape|N|Y/);
}

/**
 * Renderiza botões de ação operacional para uma caixa.
 * @param {object} box
 * @param {string} dataIdAttr - JSON stringificado [company_id, output_id, box_id]
 * @returns {string}
 */
export function renderBoxActionButtons(box, dataIdAttr) {
	const status = Number(box.status);
	const parts = [];
	parts.push(`<button type="button" class="btn-pkbox-detail link-detail-box" data-id='${dataIdAttr}' data-code="${escHtml(box.code)}" style="font-size:0.78em;padding:1px 5px;margin:1px;cursor:pointer">Detalhar</button>`);
	if (status === 10) {
		parts.push(`<button class="btn-pkbox-add-items" data-id='${dataIdAttr}' data-code="${escHtml(box.code)}" style="font-size:0.78em;padding:1px 5px;margin:1px;cursor:pointer">Adicionar Itens</button>`);
		parts.push(`<button class="btn-pkbox-close" data-id='${dataIdAttr}' data-code="${escHtml(box.code)}" data-items="${box.items?.length || box.total_items || 0}" style="font-size:0.78em;padding:1px 5px;margin:1px;cursor:pointer">Lacrar Caixa</button>`);
	}
	if (status === 10 || status === 20) {
		parts.push(`<button class="btn-pkbox-cancel" data-id='${dataIdAttr}' data-code="${escHtml(box.code)}" style="font-size:0.78em;padding:1px 5px;margin:1px;cursor:pointer">Cancelar Caixa</button>`);
	}
	return parts.join(' ') || '—';
}

/**
 * Vincula eventos de ação operacional em um container.
 * @param {Element} container
 * @param {function} onUpdate
 */
export function bindBoxActionEvents(container, onUpdate) {
	if (!container) return;

	container.querySelectorAll('.btn-pkbox-add-items').forEach(el => {
		el.addEventListener('click', async (e) => {
			e.preventDefault();
			e.stopPropagation();
			try {
				const [companyId, outputId, boxId] = JSON.parse(el.getAttribute('data-id'));
				const boxCode = el.getAttribute('data-code') || boxId;
				await openAddItemsModal(companyId, outputId, boxId, boxCode, onUpdate);
			} catch (err) {
				notify(err?.message || String(err), 'Erro');
			}
		});
	});

	container.querySelectorAll('.btn-pkbox-close').forEach(el => {
		el.addEventListener('click', async (e) => {
			e.preventDefault();
			e.stopPropagation();
			try {
				const [companyId, outputId, boxId] = JSON.parse(el.getAttribute('data-id'));
				const boxCode = el.getAttribute('data-code') || boxId;
				const itemCount = parseInt(el.getAttribute('data-items') || '0', 10);
				openCloseBoxModal(companyId, outputId, boxId, boxCode, itemCount, onUpdate);
			} catch (err) {
				notify(err?.message || String(err), 'Erro');
			}
		});
	});

	container.querySelectorAll('.btn-pkbox-cancel, .btn-cancel-box, .pick-box-cancel').forEach(el => {
		el.addEventListener('click', async (e) => {
			e.preventDefault();
			e.stopPropagation();
			try {
				const [companyId, outputId, boxId] = JSON.parse(el.getAttribute('data-id'));
				const boxCode = el.getAttribute('data-code') || el.getAttribute('data-box-code') || boxId;
				openCancelBoxModal(companyId, outputId, boxId, boxCode, onUpdate);
			} catch (err) {
				notify(err?.message || String(err), 'Erro');
			}
		});
	});
}

/**
 * Modal completo de caixas de picking para uma saída (lista + operações).
 * @param {string} companyId
 * @param {number|string} outputId
 * @param {function} [onUpdate]
 * @param {{ title?: string, showCreate?: boolean }} [opts]
 */
export async function openPickingBoxesForOutputModal(companyId, outputId, onUpdate, opts = {}) {
	const title = opts.title || `Caixas de Picking — Saída #${outputId}`;
	const showCreate = opts.showCreate !== false;

	async function loadAndRender(container) {
		container.innerHTML = '<p style="color:#888;padding:0.5rem 0">Carregando caixas...</p>';
		try {
			const data = await api.call(api.list_output_picking_boxes, companyId, outputId);
			const boxes = data?.boxes || [];

			const rows = boxes.length
				? boxes.map(box => {
					const statusLabel = STATUS_BOX_LABEL[box.status] || box.status;
					const statusStyle = STATUS_BOX_STYLE[box.status] || '';
					const dataId = escHtml(JSON.stringify([companyId, outputId, box.id]));
					const itemCount = box.items?.length || box.total_items || 0;
					const qtyTotal = box.total_quantity
						|| box.items?.reduce?.((s, i) => s + (Number(i.quantity) || 0), 0)
						|| 0;
					const actions = renderBoxActionButtons({ ...box, total_items: itemCount }, dataId);
					return `<tr>
						<td><a class="pick-box-link" data-box-id="${escHtml(box.id)}" href="#" style="cursor:pointer;text-decoration:underline dotted">${escHtml(box.code)}</a></td>
						<td>${escHtml(box.seal_code || '—')}</td>
						<td><span style="display:inline-block;padding:2px 7px;border-radius:3px;font-size:0.82em;font-weight:600;${statusStyle}">${statusLabel}</span></td>
						<td>${fmtDt(box.opened_at)}</td>
						<td style="text-align:right">${itemCount}</td>
						<td style="text-align:right">${qtyTotal}</td>
						<td style="white-space:nowrap">${actions}</td>
					</tr>`;
				}).join('')
				: '';

			container.innerHTML = `
				<p style="margin:0 0 0.5rem;color:#666;font-size:0.85em">${escHtml(BOX_CTRL_HINT)}</p>
				${boxes.length ? `
				<table style="width:100%;border-collapse:collapse;font-size:0.88em">
					<thead>
						<tr style="background:#f0f0f0">
							<th style="padding:4px 6px;text-align:left">Caixa</th>
							<th style="padding:4px 6px;text-align:left">Lacre</th>
							<th style="padding:4px 6px;text-align:left">Status</th>
							<th style="padding:4px 6px;text-align:left">Aberta em</th>
							<th style="padding:4px 6px;text-align:right">Itens</th>
							<th style="padding:4px 6px;text-align:right">Qtd. Total</th>
							<th style="padding:4px 6px;text-align:left">Ações</th>
						</tr>
					</thead>
					<tbody>${rows}</tbody>
				</table>` : '<p style="color:#888;font-style:italic">Nenhuma caixa registrada para esta saída.</p>'}`;

			container.querySelectorAll('.pick-box-link').forEach(el => {
				el.addEventListener('click', async (e) => {
					e.preventDefault();
					const boxId = el.getAttribute('data-box-id');
					const { default: openPickingBoxDetailModal } = await import('../forms/output-picking-box-detail.js');
					await openPickingBoxDetailModal(companyId, outputId, boxId, async () => {
						await loadAndRender(container);
						if (typeof onUpdate === 'function') onUpdate();
					});
				});
			});

			const refresh = async () => {
				await loadAndRender(container);
				if (typeof onUpdate === 'function') onUpdate();
			};
			bindBoxActionEvents(container, refresh);

		} catch (err) {
			container.innerHTML = `<p style="color:#c00">Erro ao carregar: ${escHtml(err?.message || String(err))}</p>`;
		}
	}

	const createBtn = showCreate
		? `<button type="button" class="button mtl btn-pkbox-open-new">Abrir Caixa</button>`
		: '';

	const html = `
		<div class="msgbox" style="min-width:680px;max-width:960px">
			<div class="mb-title">${escHtml(title)}</div>
			<div class="mb-text" id="pick-boxes-container" style="max-height:70vh;overflow-y:auto;min-height:60px">
				<p style="color:#888">Carregando...</p>
			</div>
			${createBtn}
			<button type="button" class="button mtl bn_N">Fechar</button>
		</div>
	`;

	const dlg = dialog.create(html, (key) => {
		if (key === 'Escape' || key === 'N') dlg.destroy();
	}, /Escape|N/);

	const node = dlg.getNode();
	const container = node.querySelector('#pick-boxes-container');
	if (container) await loadAndRender(container);

	const openBtn = node.querySelector('.btn-pkbox-open-new');
	if (openBtn) {
		openBtn.addEventListener('click', () => {
			openCreateBoxModal(companyId, outputId, async () => {
				if (container) await loadAndRender(container);
				if (typeof onUpdate === 'function') onUpdate();
			});
		});
	}
}
